import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { useAuthStore } from '../stores/auth';
import { useAnonymousSessionStore } from '../stores/anonymousSession';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'dashboard',
    component: () => import('../pages/DashboardPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/quickstart',
    name: 'quickstart',
    component: () => import('../pages/QuickstartPage.vue'),
    meta: { requiresAuth: true },
  },
  // AI-first onboarding (per ADR-0034). The legacy `/quickstart` stays for
  // engineering-bypass / direct linking but `/onboarding` is the new default
  // for users without a project.
  {
    path: '/onboarding',
    name: 'onboarding-entry',
    component: () => import('../pages/OnboardingEntryPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/onboarding/goals/:projectId',
    name: 'onboarding-goals',
    component: () => import('../pages/GoalsDialogPage.vue'),
    props: (route) => ({ projectId: String(route.params.projectId) }),
    meta: { requiresAuth: true, wide: true },
  },
  {
    path: '/projects/new',
    name: 'project-new',
    component: () => import('../pages/ProjectEditPage.vue'),
    props: () => ({ projectId: null }),
    meta: { requiresAuth: true },
  },
  {
    path: '/projects/:projectId',
    name: 'project-detail',
    component: () => import('../pages/ProjectDetailPage.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  // Per ADR-0036 / ADR-0037. Deep-link target carried in the
  // `test_call_refinement_available` and `goal_refinement_available`
  // notifications. Renders the same ProjectDetailPage but with an
  // `openConversationId` prop that auto-opens the Copilot Sheet on
  // that specific conversation. Keeps the URL meaningful and
  // shareable instead of stuffing the id into a query string.
  {
    path: '/projects/:projectId/copilot/:conversationId',
    name: 'project-detail-copilot',
    component: () => import('../pages/ProjectDetailPage.vue'),
    props: (route) => ({
      projectId: route.params.projectId,
      openConversationId: route.params.conversationId,
    }),
    meta: { requiresAuth: true },
  },
  {
    path: '/projects/:projectId/edit',
    name: 'project-edit',
    component: () => import('../pages/ProjectEditPage.vue'),
    props: (route) => ({ projectId: route.params.projectId }),
    meta: { requiresAuth: true },
  },
  // Per ADR-0040 / philosophy §1.4. AI-first scenario creation is the default
  // when the user clicks "+ Создать сценарий". The form-based engineering
  // bypass lives at /projects/:projectId/scenarios/new/form.
  {
    path: '/projects/:projectId/scenarios/new',
    name: 'scenario-new',
    component: () => import('../pages/ScenarioCreationPage.vue'),
    props: (route) => ({ projectId: String(route.params.projectId) }),
    meta: { requiresAuth: true, wide: true },
  },
  {
    path: '/projects/:projectId/scenarios/new/form',
    name: 'scenario-new-form',
    component: () => import('../pages/ScenarioEditPage.vue'),
    props: (route) => ({ projectId: route.params.projectId, scenarioId: null }),
    meta: { requiresAuth: true },
  },
  // Per ADR-0040 / philosophy §1.4 recursion. AI-first goal creation —
  // second instantiation of the creation contract after scenarios.
  // The form-based bypass would live at /projects/:projectId/goals/new/form
  // when the Goals tab on ProjectDetailPage ships (§9 open item); until
  // then the AI surface is the only way in.
  {
    path: '/projects/:projectId/goals/new',
    name: 'goal-new',
    component: () => import('../pages/GoalCreationPage.vue'),
    props: (route) => ({ projectId: String(route.params.projectId) }),
    meta: { requiresAuth: true, wide: true },
  },
  // Per ADR-0040 §1.4 (form-bypass). Engineering-layer alternative to
  // the AI-first goal creation surface for power users who know the
  // success_criteria JSON shape.
  {
    path: '/projects/:projectId/goals/new/form',
    name: 'goal-new-form',
    component: () => import('../pages/GoalCreationFormPage.vue'),
    props: (route) => ({ projectId: String(route.params.projectId) }),
    meta: { requiresAuth: true },
  },
  // Per ADR-0062 / philosophy §1.4 recursion. AI-first plugin wiring —
  // third instantiation of the creation contract from ADR-0040. Sits
  // on top of the ADR-0061 plugin SPI foundation.
  {
    path: '/projects/:projectId/plugin-configurations/new',
    name: 'plugin-configuration-new',
    component: () => import('../pages/PluginCreationPage.vue'),
    props: (route) => ({ projectId: String(route.params.projectId) }),
    meta: { requiresAuth: true, wide: true },
  },
  // Per ADR-0061 §"Engineering-bypass form surface". Form-driven
  // alternative for power users who know the implementation id +
  // schema. The AI surface above is the default.
  {
    path: '/projects/:projectId/plugin-configurations/new/form',
    name: 'plugin-configuration-new-form',
    component: () => import('../pages/PluginConfigurationFormPage.vue'),
    props: (route) => ({ projectId: String(route.params.projectId) }),
    meta: { requiresAuth: true },
  },
  {
    path: '/scenarios/:scenarioId',
    name: 'scenario-edit',
    component: () => import('../pages/ScenarioEditPage.vue'),
    props: (route) => ({ projectId: null, scenarioId: route.params.scenarioId }),
    meta: { requiresAuth: true },
  },
  {
    path: '/scenarios/:scenarioId/prompt',
    name: 'scenario-prompt-edit',
    component: () => import('../pages/ScenarioPromptEditorPage.vue'),
    props: (route) => ({ scenarioId: String(route.params.scenarioId) }),
    meta: { requiresAuth: true, wide: true },
  },
  {
    path: '/scripts/:scriptId',
    name: 'script-edit',
    component: () => import('../pages/ScriptEditorPage.vue'),
    props: (route) => ({ scriptId: String(route.params.scriptId) }),
    meta: { requiresAuth: true, wide: true },
  },
  {
    path: '/steps/:stepId/prompt',
    name: 'step-prompt-edit',
    component: () => import('../pages/StepPromptEditorPage.vue'),
    props: (route) => ({ stepId: String(route.params.stepId) }),
    meta: { requiresAuth: true, wide: true },
  },
  {
    path: '/sessions/:sessionId',
    name: 'session-detail',
    component: () => import('../pages/SessionDetailPage.vue'),
    props: (route) => ({ sessionId: route.params.sessionId }),
    meta: { requiresAuth: true },
  },
  {
    path: '/calls',
    name: 'calls',
    component: () => import('../pages/CallsPage.vue'),
    meta: { requiresAuth: true, wide: true },
  },
  {
    path: '/reports',
    redirect: '/reports/telephony',
  },
  {
    path: '/reports/telephony',
    name: 'reports-telephony',
    component: () => import('../pages/ReportsTelephonyPage.vue'),
    meta: { requiresAuth: true, wide: true },
  },
  // Tenant-level outbound SIP trunks (ADR-0025 cascade fallback + ADR-0051).
  // Project-scoped SIP remains on Project Settings; this page manages the
  // tenant-default trunk used when a Project has no own trunk.
  {
    path: '/settings/sip-trunks',
    name: 'tenant-sip-trunks',
    component: () => import('../pages/TenantSipTrunksPage.vue'),
    meta: { requiresAuth: true, wide: true },
  },
  {
    path: '/industry-packs',
    name: 'industry-packs',
    component: () => import('../pages/IndustryPacksPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Tenant-wide webhooks dashboard (sidebar entry is dev-mode only,
    // but the route itself doesn't gate — opening a saved URL works
    // regardless of the dev toggle).
    path: '/webhooks',
    name: 'webhooks-aggregator',
    component: () => import('../pages/WebhooksAggregatorPage.vue'),
    meta: { requiresAuth: true, wide: true },
  },
  {
    // Tenant-wide API keys dashboard. Same gating notes as /webhooks.
    path: '/api-keys',
    name: 'api-keys-aggregator',
    component: () => import('../pages/ApiKeysAggregatorPage.vue'),
    meta: { requiresAuth: true, wide: true },
  },
  {
    // Per ADR-0022. Tenant-facing billing — wallet, cards, history, invoices.
    path: '/billing',
    name: 'billing',
    component: () => import('../pages/BillingPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    // In-product Support / Professional Services / Sales form. Posts to
    // the same public `POST /v1/support` endpoint as the marketing site's
    // contact form. Auth not required at the route level — a signed-in
    // session pre-fills name/email and the route lives behind the
    // dashboard shell, so we keep the requiresAuth flag for consistency.
    path: '/support',
    name: 'support',
    component: () => import('../pages/SupportPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/industry-packs/:idn',
    name: 'industry-pack-detail',
    component: () => import('../pages/IndustryPackDetailPage.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    // Per ADR-0017: admin Economics overview (GM%, fallback, provider
    // mix). Gated to platform admins via `meta.requiresPlatformAdmin`
    // — the router guard below redirects non-admin away. The backend
    // `admin/economics/*` surface enforces the same check via
    // `requirePlatformAdmin(ctx)`; this UX-only gate prevents the SPA
    // from rendering the page just to immediately 403 on data fetch.
    path: '/admin/economics',
    name: 'admin-economics',
    component: () => import('../pages/admin/EconomicsPage.vue'),
    meta: { requiresAuth: true, requiresPlatformAdmin: true },
  },
  {
    // Per ADR-0023 §2-3: pack editor backbone (admin only).
    path: '/admin/industry-packs',
    name: 'admin-industry-packs',
    component: () => import('../pages/admin/IndustryPackAdminListPage.vue'),
    meta: { requiresAuth: true, requiresPlatformAdmin: true },
  },
  {
    path: '/admin/industry-pack-versions/:versionId/edit',
    name: 'admin-industry-pack-version-edit',
    component: () => import('../pages/admin/IndustryPackVersionEditorPage.vue'),
    props: (route) => ({ versionId: String(route.params.versionId) }),
    meta: { requiresAuth: true, requiresPlatformAdmin: true, wide: true },
  },
  {
    path: '/admin/industry-pack-versions/:versionId/pinned',
    name: 'admin-industry-pack-version-pinned',
    component: () => import('../pages/admin/IndustryPackPinnedProjectsPage.vue'),
    props: (route) => ({ versionId: String(route.params.versionId) }),
    meta: { requiresAuth: true, requiresPlatformAdmin: true },
  },
  {
    // Cross-tenant projects view for platform admins. Backend
    // `GET /v1/admin/projects` is gated server-side; this UX gate
    // mirrors it.
    path: '/admin/projects',
    name: 'admin-projects',
    component: () => import('../pages/admin/AdminProjectsPage.vue'),
    meta: { requiresAuth: true, requiresPlatformAdmin: true, wide: true },
  },
  {
    // Per ADR-0022 §9. Time-versioned customer-facing rate cards.
    path: '/admin/billing/rate-cards',
    name: 'admin-billing-rate-cards',
    component: () => import('../pages/admin/RateCardsPage.vue'),
    meta: { requiresAuth: true, requiresPlatformAdmin: true, wide: true },
  },
  {
    // Per ADR-0022 §9. Tenant search → mode/sandbox/credit-limit patch,
    // sandbox promotion, manual balance adjustment.
    path: '/admin/billing/tenants',
    name: 'admin-billing-tenants',
    component: () => import('../pages/admin/TenantsBillingPage.vue'),
    meta: { requiresAuth: true, requiresPlatformAdmin: true, wide: true },
  },
  {
    // Static-code "test users" for App Store / Google Play review.
    // Backend `admin/users/test-users` is gated server-side; this UX
    // gate mirrors it.
    path: '/admin/test-users',
    name: 'admin-test-users',
    component: () => import('../pages/admin/TestUsersPage.vue'),
    meta: { requiresAuth: true, requiresPlatformAdmin: true, wide: true },
  },
  {
    // Docs are public — readable without login. The sidebar entry is still
    // gated behind the authenticated shell, but anyone with the URL can
    // read. This also avoids an auth-guard bounce when a saved tab loads
    // before the auth store rehydrates.
    path: '/docs/:slug?',
    name: 'docs',
    component: () => import('../pages/DocsPage.vue'),
    props: true,
    meta: { wide: true },
  },
  {
    // Per ADR-0031. Accept-invitation landing page. Public — preview
    // works without auth so the recipient sees what they're accepting
    // before signing in. The page itself redirects to /login when the
    // user isn't signed in (see AcceptInvitationPage.vue).
    path: '/invitations/:token/accept',
    name: 'accept-invitation',
    component: () => import('../pages/AcceptInvitationPage.vue'),
    props: true,
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../pages/LoginPage.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => {
  const auth = useAuthStore();
  // Per ADR-0073. Onboarding routes (`/onboarding` + `/onboarding/goals/:projectId`)
  // can run pre-auth when the marketing site has handed the visitor an
  // engagement token. The store was hydrated once at boot from the
  // initial URL's query params; we accept either an active anonymous
  // session OR a signed-in user for these routes.
  const anon = useAnonymousSessionStore();
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    if (to.path.startsWith('/onboarding') && anon.isActive) {
      return true;
    }
    return { name: 'login', query: { next: to.fullPath } };
  }
  if (to.meta.requiresPlatformAdmin && !auth.isPlatformAdmin) {
    // Bounce non-admins back to the dashboard rather than rendering a
    // page that's about to 403 on its first data fetch.
    return { name: 'dashboard' };
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'dashboard' };
  }
  return true;
});

/**
 * Stale-tab-after-deploy recovery.
 *
 * Vite hashes lazy chunk filenames, so after a deploy the open tab's
 * `index.html` references chunks that no longer exist — the next route
 * change rejects with `TypeError: Failed to fetch dynamically imported
 * module` (Chrome/Edge) or `error loading dynamically imported module`
 * (Safari/Firefox). We bounce through a one-shot location.replace so
 * the new `index.html` is fetched. The sessionStorage flag stops a
 * reload loop if the failure is something else (e.g. CSP, offline).
 */
const RELOAD_FLAG = 'avp.spa.reload-on-chunk-error';
router.onError((err, to) => {
  const message = err instanceof Error ? err.message : String(err);
  const isChunkLoadError =
    /dynamically imported module/i.test(message) ||
    /Loading chunk/i.test(message) ||
    /Failed to fetch/i.test(message) ||
    (err as { name?: string }).name === 'ChunkLoadError';
  if (!isChunkLoadError) return;
  if (window.sessionStorage.getItem(RELOAD_FLAG)) return;
  window.sessionStorage.setItem(RELOAD_FLAG, '1');
  // Navigate to the intended target after the reload.
  window.location.replace(to.fullPath);
});

// Vite emits this on `<link rel="modulepreload">` failures before the
// router knows about them. Same recovery path.
window.addEventListener('vite:preloadError', (e) => {
  if (window.sessionStorage.getItem(RELOAD_FLAG)) return;
  window.sessionStorage.setItem(RELOAD_FLAG, '1');
  e.preventDefault();
  window.location.reload();
});

// Once a navigation succeeds we know the new chunks are loadable; clear
// the flag so a *future* deploy can trigger the reload again.
router.afterEach(() => {
  window.sessionStorage.removeItem(RELOAD_FLAG);
});
